// The signed-in dashboard's sidebar (revamp PR 3, direction B): the dark rail with the app's nav tree.
// The tree, its filters and the module locks come from the old rail's hooks (sidebar/use-nav-tree.ts);
// this file only decides what is active and open and renders it. The frame (AppFrame) owns collapse,
// ⌘B, the persisted preference and the mobile drawer. Account, theme, language, help, changelog,
// Pengaturan, logout and the quick search live in the top bar.
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import FeatureLockedModal from '@components/feature-lock/FeatureLockedModal';
import { useLanguage } from '@/contexts/LanguageContext';
import { sidebarAnchor, sidebarSubAnchor, useOptionalTour } from '@/features/onboarding';
import { HAPTIC, haptic, useHaptic } from '@/utils/haptic';
import type { FrameState } from './AppFrame';
import { activeLinkIndex, isLeafActive } from './sidebar/nav-active';
import { SidebarNavGroup, SidebarNavItem, SidebarNavList } from './sidebar/SidebarNav';
import { SidebarRail } from './sidebar/SidebarRail';
import { BADGE_KEYS, type NavEntry, useNavTree } from './sidebar/use-nav-tree';

/** Label of the group that owns the route, if any. */
function activeGroupOf(items: NavEntry[], pathname: string, search: string) {
  return items.find((item) => item.subLinks && activeLinkIndex(pathname, search, item.subLinks) >= 0)?.label;
}

export function DashboardSidebar(frame: FrameState) {
  const { collapsed, isDesktop, setMobileOpen } = frame;
  const { t } = useLanguage();
  const { pathname, search } = useLocation();
  const { items, isGatewayUser } = useNavTree();
  const hapticNav = useHaptic('light');
  const hapticToggle = useHaptic('medium');
  const [lockedModuleLabel, setLockedModuleLabel] = useState<string | null>(null);
  const tour = useOptionalTour();

  // Groups the user opened, plus the one the route is in. Entering a group's route opens it; nothing
  // closes a group except the user (the old rail's rule).
  const activeGroup = activeGroupOf(items, pathname, search);
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set(activeGroup ? [activeGroup] : []));
  useEffect(() => {
    if (!activeGroup) return;
    setOpenGroups((prev) => (prev.has(activeGroup) ? prev : new Set(prev).add(activeGroup)));
  }, [activeGroup]);

  const setGroupOpen = (label: string, open: boolean) => {
    hapticToggle();
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (open) next.add(label);
      else next.delete(label);
      return next;
    });
  };

  const onNavigate = () => {
    hapticNav();
    if (!isDesktop) setMobileOpen(false);
  };

  // A light buzz while the nav scrolls, throttled (touch devices; the old rail did the same).
  const lastScrollBuzz = useRef(0);
  const onNavScroll = () => {
    const now = Date.now();
    if (now - lastScrollBuzz.current > 140) {
      lastScrollBuzz.current = now;
      haptic(HAPTIC.light);
    }
  };

  const labelOf = (entry: { label: string; i18nKey?: string }) => (entry.i18nKey ? t(entry.i18nKey) : entry.label);
  const badgeOf = (badge?: string) => (badge ? (BADGE_KEYS[badge] ? t(BADGE_KEYS[badge]) : badge) : undefined);

  const gatewayLogo = isGatewayUser ? (
    <img
      src="/image/jpg/gatewaycontainerline_logo.jpeg"
      alt="Gateway"
      className={collapsed ? 'size-10 rounded-control object-contain' : 'h-8 w-auto max-w-25 rounded-control'}
    />
  ) : undefined;

  return (
    <SidebarRail {...frame} logoId="dashboard-rail" brandExtra={gatewayLogo} onNavScroll={onNavScroll}>
      <SidebarNavList label={t('lumen.sidebar.navLabel')} tourAnchor="nav-list">
        {items.map((item) => {
          const label = labelOf(item);
          const tourAnchor = sidebarAnchor(item.label);

          // Locked module: one dimmed row whatever the kind; clicking explains how to activate it.
          if (item.locked) {
            return (
              <SidebarNavItem
                key={item.label}
                label={label}
                icon={item.icon}
                collapsed={collapsed}
                locked
                lockedLabel={t('lumen.sidebar.locked')}
                onLockedClick={() => {
                  hapticNav();
                  // The menu tour lets the user click its rows; the lock dialog would open under the tour's
                  // dim, out of reach. End the tour first, as a `dismissOnClick` step does.
                  tour?.stopTour();
                  setLockedModuleLabel(item.label);
                }}
                tourAnchor={tourAnchor}
              />
            );
          }

          if (item.subLinks) {
            const activeIndex = activeLinkIndex(pathname, search, item.subLinks);
            return (
              <SidebarNavGroup
                key={item.label}
                label={label}
                icon={item.icon}
                collapsed={collapsed}
                active={activeIndex >= 0}
                badge={badgeOf(item.badge)}
                open={openGroups.has(item.label)}
                onOpenChange={(open) => setGroupOpen(item.label, open)}
                items={item.subLinks.map((sub, index) => ({
                  to: sub.link,
                  label: labelOf(sub),
                  icon: sub.icon,
                  active: index === activeIndex,
                  tourAnchor: sidebarSubAnchor(item.label, sub.label),
                }))}
                onNavigate={onNavigate}
                tourAnchor={tourAnchor}
              />
            );
          }

          return (
            <SidebarNavItem
              key={item.label}
              label={label}
              icon={item.icon}
              collapsed={collapsed}
              to={item.link}
              active={isLeafActive(pathname, item.link)}
              badge={badgeOf(item.badge)}
              onNavigate={onNavigate}
              tourAnchor={tourAnchor}
            />
          );
        })}
      </SidebarNavList>
      <FeatureLockedModal
        opened={lockedModuleLabel !== null}
        onClose={() => setLockedModuleLabel(null)}
        moduleLabel={lockedModuleLabel ?? undefined}
      />
    </SidebarRail>
  );
}
